import React from 'react';
import { MantineProvider, Container, Title, Text, Button, Box, createTheme, rem, Group, Image, SimpleGrid, Stack } from '@mantine/core';
import '@mantine/core/styles.css';

const theme = createTheme({
  primaryColor: 'blue',
});

// Sample video data
const videoGallery = [
  {
    title: 'Creative Storytelling',
    thumbnail: 'linear-gradient(135deg, #FF6B6B, #4ECDC4)',
    duration: '2:30'
  },
  {
    title: 'Business Presentation',
    thumbnail: 'linear-gradient(135deg, #A8E6CF, #DCEDC1)',
    duration: '3:45'
  },
  {
    title: 'Product Showcase',
    thumbnail: 'linear-gradient(135deg, #FFD93D, #FF6B6B)',
    duration: '1:55'
  },
  {
    title: 'Educational Content',
    thumbnail: 'linear-gradient(135deg, #6C5CE7, #A8E6CF)',
    duration: '4:20'
  },
  {
    title: 'Social Media Ad',
    thumbnail: 'linear-gradient(135deg, #FF8B8B, #FFD93D)',
    duration: '0:45'
  },
  {
    title: 'Travel Vlog',
    thumbnail: 'linear-gradient(135deg, #4ECDC4, #6C5CE7)',
    duration: '5:15'
  }
];

// Feature section data
const features = [
  {
    title: "Create without constraints",
    description: "Type your idea, add the specifics—like length, platform, voiceover accent and get a video perfectly tailored to your vision.",
    gradient: "linear-gradient(135deg, #FF6B6B, #4ECDC4)",
  },
  {
    title: "Creativity at your command",
    description: "Generate stunning visuals, compelling narratives, and professional-quality videos with just a few clicks.",
    gradient: "linear-gradient(135deg, #8B5CF6, #EC4899)",
  },
  {
    title: "Smart video generation",
    description: "Our AI understands context, style, and pacing to create videos that capture your audience's attention.",
    gradient: "linear-gradient(135deg, #3B82F6, #10B981)",
  }
];

// Additional showcase videos
const showcaseVideos = [
  {
    title: 'Anime-Style Story',
    description: 'A stunning anime-inspired narrative with dynamic visuals',
    thumbnail: 'linear-gradient(135deg, #FF0844, #FFB199)',
    category: 'Animation',
    views: '12.5K'
  },
  {
    title: 'Colorful Travel Journey',
    description: 'Exploring vibrant streets and cultural experiences',
    thumbnail: 'linear-gradient(135deg, #0093E9, #80D0C7)',
    category: 'Travel',
    views: '8.2K'
  },
  {
    title: 'Tech Product Launch',
    description: 'Modern and sleek product presentation',
    thumbnail: 'linear-gradient(135deg, #08AEEA, #2AF598)',
    category: 'Marketing',
    views: '15.1K'
  },
  {
    title: 'Fashion Collection',
    description: 'Elegant showcase of latest fashion trends',
    thumbnail: 'linear-gradient(135deg, #FEE140, #FA709A)',
    category: 'Fashion',
    views: '10.3K'
  }
];

// Platform use cases data
const platformUseCases = [
  {
    platform: "YouTube Shorts",
    icon: "🎥",
    title: "Stand out on YouTube Shorts",
    description: "Create vertical, attention-grabbing shorts that keep viewers engaged. Perfect for storytelling and building your YouTube presence.",
    benefits: ["60-second format optimization", "Trending music integration", "Custom thumbnails", "Vertical video focus"],
    gradient: "linear-gradient(135deg, #FF0000, #FF6B6B)"
  },
  {
    platform: "Instagram Reels",
    icon: "📱",
    title: "Dominate Instagram Reels",
    description: "Create polished, professional Reels that capture attention in the Instagram feed and explore page.",
    benefits: ["Instagram-optimized effects", "Music library access", "Story integration", "Multi-clip editing"],
    gradient: "linear-gradient(135deg, #833AB4, #FD1D1D)"
  },
  {
    platform: "TikTok",
    icon: "🎵",
    title: "Go Viral on TikTok",
    description: "Create trending TikTok content that resonates with your audience and rides the latest waves.",
    benefits: ["Trend-based templates", "Sound sync features", "Filter effects", "Quick editing tools"],
    gradient: "linear-gradient(135deg, #00F2EA, #FF0050)"
  }
];

// Pricing data
const pricingPlans = [
  {
    name: "Free",
    price: { monthly: 0, yearly: 0 },
    features: [
      { text: "10 mins/wk of AI generation", included: true },
      { text: "iStock", included: false },
      { text: "Generative credits", included: false },
      { text: "10 GB storage", included: true },
      { text: "4 exports/wk with invideo logo", included: true },
      { text: "2.5M+ standard media", included: true }
    ],
    buttonText: "Try for free",
    buttonVariant: "filled",
    buttonStyle: { background: 'black' }
  },
  {
    name: "Plus",
    price: { monthly: 28, yearly: 336 },
    features: [
      { text: "50 mins/mo of AI generation", included: true },
      { text: "80/mo iStock", included: true },
      { text: "60s Generative credits", included: true },
      { text: "100 GB storage", included: true },
      { text: "Unlimited exports", included: true },
      { text: "2 voice clones", included: true }
    ],
    buttonText: "Get Plus",
    buttonVariant: "filled",
    buttonStyle: { background: '#0066FF' }
  },
  {
    name: "Max",
    price: { monthly: 48, yearly: 576 },
    features: [
      { text: "200 mins/mo of AI generation", included: true },
      { text: "320/mo iStock", included: true },
      { text: "60s Generative credits", included: true },
      { text: "400 GB storage", included: true },
      { text: "Unlimited exports", included: true },
      { text: "5 voice clones", included: true }
    ],
    buttonText: "Get Max",
    buttonVariant: "filled",
    buttonStyle: { background: '#0066FF' }
  },
  {
    name: "Generative",
    price: { monthly: 96, yearly: 1152 },
    features: [
      { text: "200 mins/mo of AI generation", included: true },
      { text: "320/mo iStock", included: true },
      { text: "15m Generative credits", included: true },
      { text: "400 GB storage", included: true },
      { text: "Unlimited exports", included: true },
      { text: "5 voice clones", included: true }
    ],
    buttonText: "Get Generative",
    buttonVariant: "filled",
    buttonStyle: { background: '#0066FF' },
    bestValue: true
  }
];

function App() {
  const [isYearly, setIsYearly] = React.useState(false);

  return (
    <MantineProvider theme={theme}>
      {/* Navigation Bar */}
      <Box
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          backdropFilter: 'blur(10px)',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          zIndex: 1000,
          padding: rem(16),
          borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
        }}
      >
        <Container size="lg">
          <Group justify="space-between" align="center">
            <Group>
              {/* Logo */}
              <Group gap={rem(8)} align="center">
                <Box
                  style={{
                    width: rem(32),
                    height: rem(32),
                    borderRadius: '50%',
                    background: 'linear-gradient(45deg, #8B5CF6, #EC4899)',
                  }}
                />
                <Text fw={700} size="lg">youvidgen AI</Text>
              </Group>
            </Group>

            {/* Navigation Links */}
            <Group gap={rem(32)}>
              <Text style={{ cursor: 'pointer' }}>youvidgen AI</Text>
              <Text style={{ cursor: 'pointer' }}>youvidgen Studio</Text>
              <Text style={{ cursor: 'pointer' }}>Help</Text>
              <Text style={{ cursor: 'pointer' }}>Community</Text>
              <Text style={{ cursor: 'pointer' }}>Pricing</Text>
            </Group>

            {/* Auth Buttons */}
            <Group>
              <Button variant="subtle">Login</Button>
              <Button
                style={{
                  background: 'linear-gradient(45deg, #8B5CF6, #EC4899)',
                  border: 'none'
                }}
              >
                Sign up
              </Button>
            </Group>
          </Group>
        </Container>
      </Box>

      {/* Main Content */}
      <Container size="lg" py={rem(140)}>
        <Box ta="center" mb={rem(50)}>
          <Title
            order={1}
            size="h1"
            style={{ fontSize: rem(72) }}
            mb={rem(20)}
          >
            Bring your <span style={{ background: 'linear-gradient(to right, #8B5CF6, #EC4899)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>ideas to life</span>
          </Title>

          <Text c="dimmed" size="xl" maw={rem(600)} mx="auto" mb={rem(40)}>
            Unlock your creativity and turn any idea into a full-length video. Let your imagination take center stage!
          </Text>

          <Button
            size="xl"
            radius="md"
            style={{
              background: '#0066FF',
              padding: `${rem(24)} ${rem(48)}`,
              fontSize: rem(20),
              height: 'auto'
            }}
          >
            Try youvidgen AI
          </Button>

          <Text size="sm" c="dimmed" mt={rem(16)}>
            No credit card needed
          </Text>
        </Box>

        {/* Main Video Area */}
        <Box
          style={{
            width: '100%',
            height: rem(500),
            background: 'linear-gradient(135deg, #2D325A, #392F5A)',
            borderRadius: rem(20),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: rem(80),
          }}
        >
          <Text size="xl" c="white">Video Content Area</Text>
        </Box>

        {/* Video Gallery Section */}
        <Box mb={rem(100)}>
          <Title order={2} size="h2" ta="center" mb={rem(40)}>
            Explore What Others Have Created
          </Title>

          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing={rem(24)}>
            {videoGallery.map((video, index) => (
              <Box
                key={index}
                style={{
                  borderRadius: rem(12),
                  overflow: 'hidden',
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease',
                  ':hover': {
                    transform: 'scale(1.02)',
                  }
                }}
              >
                {/* Thumbnail */}
                <Box
                  style={{
                    background: video.thumbnail,
                    height: rem(200),
                    position: 'relative',
                  }}
                >
                  {/* Play Button Overlay */}
                  <Box
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: rem(50),
                      height: rem(50),
                      borderRadius: '50%',
                      background: 'rgba(255, 255, 255, 0.9)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      style={{
                        width: 0,
                        height: 0,
                        borderStyle: 'solid',
                        borderWidth: '8px 0 8px 16px',
                        borderColor: 'transparent transparent transparent #000',
                        marginLeft: rem(4),
                      }}
                    />
                  </Box>
                  {/* Duration Badge */}
                  <Box
                    style={{
                      position: 'absolute',
                      bottom: rem(8),
                      right: rem(8),
                      background: 'rgba(0, 0, 0, 0.7)',
                      padding: `${rem(4)} ${rem(8)}`,
                      borderRadius: rem(4),
                    }}
                  >
                    <Text size="sm" c="white">{video.duration}</Text>
                  </Box>
                </Box>
                {/* Video Title */}
                <Box p={rem(16)} style={{ background: 'white', borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
                  <Text fw={500}>{video.title}</Text>
                </Box>
              </Box>
            ))}
          </SimpleGrid>
        </Box>

        {/* Final CTA Section */}
        <Box ta="center" py={rem(80)} style={{ background: 'rgba(139, 92, 246, 0.05)', borderRadius: rem(20) }}>
          <Title order={2} size="h2" mb={rem(20)}>
            Ready to Create Your Own Video?
          </Title>
          <Text size="lg" c="dimmed" maw={rem(600)} mx="auto" mb={rem(32)}>
            Join thousands of creators who are bringing their ideas to life with youvidgen AI
          </Text>
          <Button
            size="xl"
            radius="md"
            style={{
              background: 'linear-gradient(45deg, #8B5CF6, #EC4899)',
              padding: `${rem(24)} ${rem(48)}`,
              fontSize: rem(20),
              height: 'auto',
              border: 'none',
            }}
          >
            Start Creating Now
          </Button>
        </Box>

        {/* Feature Section */}
        <Container size="lg" py={rem(100)}>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={rem(60)}>
            {/* Left side - Feature Cards */}
            <Stack gap={rem(24)}>
              {features.map((feature, index) => (
                <Box
                  key={index}
                  p={rem(32)}
                  style={{
                    borderRadius: rem(20),
                    background: 'white',
                    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.05)',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {/* Gradient Accent */}
                  <Box
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      width: rem(4),
                      height: '100%',
                      background: feature.gradient,
                    }}
                  />

                  <Title order={3} mb={rem(12)} style={{ fontSize: rem(24) }}>
                    {feature.title}
                  </Title>
                  <Text c="dimmed" size="lg">
                    {feature.description}
                  </Text>
                </Box>
              ))}
            </Stack>

            {/* Right side - Image */}
            <Box
              style={{
                borderRadius: rem(20),
                overflow: 'hidden',
                position: 'relative',
                background: 'linear-gradient(135deg, #2D325A, #392F5A)',
                minHeight: rem(500),
              }}
            >
              {/* Video Player UI */}
              <Box p={rem(16)} style={{ position: 'relative' }}>
                {/* Input Bar */}
                <Group
                  style={{
                    background: 'rgba(255, 255, 255, 0.9)',
                    padding: `${rem(8)} ${rem(16)}`,
                    borderRadius: rem(8),
                    marginBottom: rem(16),
                  }}
                >
                  <Text size="sm" style={{ flex: 1, color: '#666' }}>
                    5 min Youtube video of a man travelling through time
                  </Text>
                  <Button
                    size="sm"
                    style={{
                      background: '#4C6EF5',
                      borderRadius: rem(6),
                    }}
                  >
                    Generate
                  </Button>
                </Group>

                {/* Video Preview Area */}
                <Box
                  style={{
                    background: 'linear-gradient(135deg, #6C5CE7, #a78bfa)',
                    borderRadius: rem(12),
                    height: rem(400),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                  }}
                >
                  {/* Video Controls */}
                  <Box
                    style={{
                      position: 'absolute',
                      bottom: rem(16),
                      left: rem(16),
                      right: rem(16),
                      background: 'rgba(0, 0, 0, 0.5)',
                      padding: rem(8),
                      borderRadius: rem(8),
                      display: 'flex',
                      alignItems: 'center',
                      gap: rem(8),
                    }}
                  >
                    <Box
                      style={{
                        width: rem(24),
                        height: rem(24),
                        borderRadius: '50%',
                        background: 'white',
                      }}
                    />
                    <Box
                      style={{
                        flex: 1,
                        height: rem(4),
                        background: 'rgba(255, 255, 255, 0.3)',
                        borderRadius: rem(2),
                      }}
                    >
                      <Box
                        style={{
                          width: '50%',
                          height: '100%',
                          background: 'white',
                          borderRadius: rem(2),
                        }}
                      />
                    </Box>
                    <Text c="white" size="sm">01:05 / 02:00</Text>
                  </Box>
                </Box>
              </Box>
            </Box>
          </SimpleGrid>
        </Container>

        {/* Final Video Showcase */}
        <Container size="lg" pb={rem(100)}>
          <Box mb={rem(60)}>
            <Title ta="center" order={1} size="h1" mb={rem(50)}>
              For anyone with a <span style={{ background: 'linear-gradient(to right, #8B5CF6, #EC4899)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>story to tell</span>
            </Title>
          </Box>

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={rem(30)}>
            {showcaseVideos.map((video, index) => (
              <Box
                key={index}
                style={{
                  borderRadius: rem(20),
                  overflow: 'hidden',
                  background: 'white',
                  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.05)',
                }}
              >
                {/* Video Thumbnail */}
                <Box
                  style={{
                    background: video.thumbnail,
                    height: rem(250),
                    position: 'relative',
                    cursor: 'pointer',
                  }}
                >
                  {/* Play Button */}
                  <Box
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: rem(60),
                      height: rem(60),
                      borderRadius: '50%',
                      background: 'rgba(255, 255, 255, 0.9)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'transform 0.2s ease',
                      ':hover': {
                        transform: 'translate(-50%, -50%) scale(1.1)',
                      }
                    }}
                  >
                    <Box
                      style={{
                        width: 0,
                        height: 0,
                        borderStyle: 'solid',
                        borderWidth: '10px 0 10px 20px',
                        borderColor: 'transparent transparent transparent #000',
                        marginLeft: rem(4),
                      }}
                    />
                  </Box>

                  {/* Category Badge */}
                  <Box
                    style={{
                      position: 'absolute',
                      top: rem(16),
                      left: rem(16),
                      background: 'rgba(0, 0, 0, 0.7)',
                      padding: `${rem(4)} ${rem(12)}`,
                      borderRadius: rem(20),
                    }}
                  >
                    <Text size="sm" c="white">{video.category}</Text>
                  </Box>

                  {/* Views Badge */}
                  <Box
                    style={{
                      position: 'absolute',
                      top: rem(16),
                      right: rem(16),
                      background: 'rgba(0, 0, 0, 0.7)',
                      padding: `${rem(4)} ${rem(12)}`,
                      borderRadius: rem(20),
                    }}
                  >
                    <Text size="sm" c="white">{video.views} views</Text>
                  </Box>
                </Box>

                {/* Content */}
                <Box p={rem(24)}>
                  <Title order={3} mb={rem(8)}>{video.title}</Title>
                  <Text c="dimmed" size="md">{video.description}</Text>
                  <Group mt={rem(16)}>
                    <Button
                      variant="light"
                      style={{
                        background: 'rgba(139, 92, 246, 0.1)',
                        color: '#8B5CF6',
                      }}
                    >
                      Use this template
                    </Button>
                    <Button
                      variant="subtle"
                      style={{ color: '#666' }}
                    >
                      Learn more
                    </Button>
                  </Group>
                </Box>
              </Box>
            ))}
          </SimpleGrid>

          {/* Final CTA Button */}
          <Box ta="center" mt={rem(60)}>
            <Button
              size="xl"
              radius="md"
              style={{
                background: 'linear-gradient(45deg, #8B5CF6, #EC4899)',
                padding: `${rem(24)} ${rem(48)}`,
                fontSize: rem(20),
                height: 'auto',
                border: 'none',
              }}
            >
              Explore More Templates
            </Button>
          </Box>
        </Container>

        {/* Platform Use Cases Section */}
        <Container size="lg" py={rem(100)} style={{ background: '#f8f9fa' }}>
          <Title ta="center" order={1} size="h1" mb={rem(60)}>
            A new vision for <span style={{ background: 'linear-gradient(to right, #8B5CF6, #EC4899)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>video creation</span>
          </Title>

          <SimpleGrid cols={{ base: 1, md: 3 }} spacing={rem(30)}>
            {platformUseCases.map((platform, index) => (
              <Box
                key={index}
                p={rem(32)}
                style={{
                  background: 'white',
                  borderRadius: rem(20),
                  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.05)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* Platform Icon */}
                <Text size="xl" mb={rem(16)}>{platform.icon}</Text>

                {/* Gradient Line */}
                <Box
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: rem(4),
                    background: platform.gradient
                  }}
                />

                {/* Content */}
                <Title order={3} mb={rem(12)} style={{ fontSize: rem(24) }}>
                  {platform.title}
                </Title>
                <Text c="dimmed" size="md" mb={rem(24)}>
                  {platform.description}
                </Text>

                {/* Benefits List */}
                <Stack gap={rem(12)}>
                  {platform.benefits.map((benefit, idx) => (
                    <Group key={idx} gap={rem(8)}>
                      <Box
                        style={{
                          width: rem(20),
                          height: rem(20),
                          borderRadius: '50%',
                          background: platform.gradient,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}
                      >
                        <Text size="xs" c="white">✓</Text>
                      </Box>
                      <Text size="sm">{benefit}</Text>
                    </Group>
                  ))}
                </Stack>

                {/* Platform-specific CTA */}
                <Button
                  fullWidth
                  mt={rem(32)}
                  style={{
                    background: platform.gradient,
                    border: 'none',
                    height: 'auto',
                    padding: `${rem(12)} ${rem(24)}`,
                  }}
                >
                  Create for {platform.platform}
                </Button>
              </Box>
            ))}
          </SimpleGrid>

          {/* Social Proof */}
          <Box mt={rem(80)} style={{ background: 'white', borderRadius: rem(20), padding: rem(40) }}>
            <SimpleGrid cols={{ base: 1, md: 2 }} verticalSpacing={rem(30)} style={{ alignItems: 'center' }}>
              <Box>
                <Title order={2} size="h2" mb={rem(16)}>
                  Perfect for all skill levels
                </Title>
                <Text size="lg" c="dimmed" maw={rem(500)}>
                  youvidgen AI is perfect for all skill levels. Ditch tools with steep learning curve and create videos seamlessly with youvidgen AI.
                </Text>
                <Group mt={rem(24)}>
                  <Button
                    variant="light"
                    size="lg"
                    style={{
                      background: 'rgba(139, 92, 246, 0.1)',
                      color: '#8B5CF6',
                    }}
                  >
                    Start Creating
                  </Button>
                  <Button
                    variant="subtle"
                    size="lg"
                    style={{ color: '#666' }}
                  >
                    Watch Tutorial
                  </Button>
                </Group>
              </Box>

              {/* Testimonial Card */}
              <Box
                style={{
                  background: '#f8f9fa',
                  borderRadius: rem(16),
                  padding: rem(24),
                  position: 'relative',
                }}
              >
                <Text size="xl" mb={rem(16)}>👍</Text>
                <Text size="lg" fw={500} mb={rem(8)}>
                  "Picked up 500 followers on YouTube super fast using youvidgen"
                </Text>
                <Text size="sm" c="dimmed">
                  Pete Francis,
                </Text>
                <Text size="xs" c="dimmed">
                  FOUNDER, ONLINE REVIEWS CHANNEL
                </Text>
              </Box>
            </SimpleGrid>
          </Box>
        </Container>

        {/* Pricing Section */}
        <Container size="lg" py={rem(100)} style={{ background: 'white' }}>
          <Title ta="center" order={1} size="h1" mb={rem(16)}>
            The right <span style={{ background: 'linear-gradient(to right, #8B5CF6, #EC4899)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>plans</span>,
          </Title>
          <Title ta="center" order={1} size="h1" mb={rem(50)}>
            for the right <span style={{ background: 'linear-gradient(to right, #EC4899, #8B5CF6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>price</span>
          </Title>

          {/* Pricing Toggle */}
          <Group justify="center" mb={rem(50)}>
            <Box
              style={{
                background: '#f1f3f5',
                borderRadius: rem(30),
                padding: rem(4),
                display: 'inline-flex',
                gap: rem(4)
              }}
            >
              <Button
                variant={!isYearly ? 'filled' : 'subtle'}
                onClick={() => setIsYearly(false)}
                style={{
                  background: !isYearly ? 'white' : 'transparent',
                  color: !isYearly ? 'black' : '#666',
                  boxShadow: !isYearly ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                }}
              >
                Monthly
              </Button>
              <Button
                variant={isYearly ? 'filled' : 'subtle'}
                onClick={() => setIsYearly(true)}
                rightSection={
                  isYearly ? (
                    <Box
                      style={{
                        background: '#4C6EF5',
                        padding: `${rem(2)} ${rem(8)}`,
                        borderRadius: rem(12),
                        fontSize: rem(12),
                        color: 'white',
                      }}
                    >
                      20% off
                    </Box>
                  ) : null
                }
                style={{
                  background: isYearly ? 'white' : 'transparent',
                  color: isYearly ? 'black' : '#666',
                  boxShadow: isYearly ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                }}
              >
                Yearly
              </Button>
            </Box>
          </Group>

          {/* Pricing Cards */}
          <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing={rem(24)}>
            {pricingPlans.map((plan, index) => (
              <Box
                key={index}
                style={{
                  background: 'white',
                  borderRadius: rem(20),
                  border: '1px solid #e9ecef',
                  padding: rem(32),
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {plan.bestValue && (
                  <Box
                    style={{
                      position: 'absolute',
                      top: rem(12),
                      right: rem(-24),
                      background: '#4C6EF5',
                      color: 'white',
                      padding: `${rem(4)} ${rem(24)}`,
                      transform: 'rotate(45deg)',
                      fontSize: rem(12),
                      fontWeight: 500,
                    }}
                  >
                    Best value
                  </Box>
                )}

                <Title order={2} size="h3" mb={rem(8)}>
                  {plan.name}
                </Title>

                <Group align="baseline" mb={rem(24)}>
                  <Text size="xl" fw={700} style={{ fontSize: rem(40) }}>
                    ${isYearly ? plan.price.yearly / 12 : plan.price.monthly}
                  </Text>
                  <Text c="dimmed">/month</Text>
                </Group>

                {isYearly && plan.price.yearly > 0 && (
                  <Text size="sm" c="dimmed" mb={rem(24)}>
                    *Billed ${plan.price.yearly} yearly
                  </Text>
                )}

                <Button
                  fullWidth
                  size="lg"
                  variant={plan.buttonVariant}
                  style={{
                    ...plan.buttonStyle,
                    marginBottom: rem(32),
                  }}
                >
                  {plan.buttonText}
                </Button>

                <Stack gap={rem(16)}>
                  {plan.features.map((feature, idx) => (
                    <Group key={idx} gap={rem(8)}>
                      <Box
                        style={{
                          width: rem(20),
                          height: rem(20),
                          borderRadius: '50%',
                          background: feature.included ? '#E9ECEF' : '#F8F9FA',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {feature.included ? (
                          <Text size="xs" c="#228BE6">✓</Text>
                        ) : (
                          <Text size="xs" c="#CED4DA">×</Text>
                        )}
                      </Box>
                      <Text
                        size="sm"
                        style={{
                          color: feature.included ? 'inherit' : '#ADB5BD',
                        }}
                      >
                        {feature.text}
                      </Text>
                    </Group>
                  ))}
                </Stack>
              </Box>
            ))}
          </SimpleGrid>
        </Container>

        {/* Support Section */}
        <Container size="lg" py={rem(100)} style={{ background: '#f8f9fa' }}>
          <Title ta="center" order={1} size="h1" mb={rem(60)}>
            All the help you need, <span style={{ background: 'linear-gradient(to right, #8B5CF6, #EC4899)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>anywhere you are</span>
          </Title>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={rem(30)}>
            {/* 24/7 Support Card */}
            <Box
              style={{
                background: 'white',
                borderRadius: rem(20),
                padding: rem(40),
                height: '100%',
              }}
            >
              <Title order={2} size="h2" mb={rem(24)}>
                24×7 human support
              </Title>

              <Box
                mb={rem(24)}
                style={{
                  width: '100%',
                  height: rem(200),
                  borderRadius: rem(16),
                  overflow: 'hidden',
                  background: 'linear-gradient(135deg, #f1f3f5, #e9ecef)',
                }}
              >
                <Box
                  style={{
                    width: '100%',
                    height: '100%',
                    background: 'url(https://images.unsplash.com/photo-1516321318423-f06f85e504b3?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1740&q=80)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                />
              </Box>

              <Text size="lg" c="dimmed" mb={rem(24)}>
                We've got your back every step of the way, from idea to finished video.
              </Text>

              <Button
                variant="light"
                size="lg"
                leftSection={
                  <Box style={{ fontSize: rem(20) }}>💬</Box>
                }
                style={{
                  background: 'rgba(139, 92, 246, 0.1)',
                  color: '#8B5CF6',
                }}
              >
                Contact Support
              </Button>
            </Box>

            {/* Discord Community Card */}
            <Box
              style={{
                background: 'white',
                borderRadius: rem(20),
                padding: rem(40),
                height: '100%',
              }}
            >
              <Title order={2} size="h2" mb={rem(24)}>
                Discord community
              </Title>

              <Box
                mb={rem(24)}
                style={{
                  width: '100%',
                  height: rem(200),
                  borderRadius: rem(16),
                  overflow: 'hidden',
                  background: 'linear-gradient(135deg, #5865F2, #404EED)',
                  position: 'relative',
                }}
              >
                {/* Discord-style illustration */}
                <Box
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '80%',
                    height: '80%',
                    background: 'url(https://cdn.discordapp.com/attachments/1234567890/1234567890/discord-illustration.png)',
                    backgroundSize: 'contain',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                  }}
                />
              </Box>

              <Text size="lg" c="dimmed" mb={rem(24)}>
                Hang out with like minded creators!
              </Text>

              <Button
                variant="filled"
                size="lg"
                leftSection={
                  <Box style={{ fontSize: rem(20) }}>👥</Box>
                }
                style={{
                  background: '#5865F2',
                }}
              >
                Join Discord
              </Button>
            </Box>
          </SimpleGrid>
        </Container>

        {/* Final Big CTA Section */}
        <Box
          style={{
            background: 'linear-gradient(135deg, #f8f9fa, #ffffff)',
            padding: `${rem(120)} 0`,
            textAlign: 'center',
            borderTop: '1px solid #e9ecef',
            borderBottom: '1px solid #e9ecef',
          }}
        >
          <Container size="lg">
            <Title
              order={1}
              size="h1"
              style={{ fontSize: rem(56), marginBottom: rem(24), color: '#1a1b1e' }}
            >
              Future of video creation
              <br />
              <span style={{
                background: 'linear-gradient(to right, #8B5CF6, #EC4899)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontSize: rem(56)
              }}>
                youvidgen AI
              </span>
            </Title>

            <Text size="xl" c="dimmed" maw={700} mx="auto" mb={rem(40)}>
              Join thousands of creators who are already using youvidgen AI to bring their ideas to life
            </Text>

            <Button
              size="xl"
              radius="md"
              style={{
                background: 'linear-gradient(45deg, #8B5CF6, #EC4899)',
                padding: `${rem(24)} ${rem(48)}`,
                fontSize: rem(20),
                height: 'auto',
                border: 'none',
              }}
            >
              Get started - it's free!
            </Button>
          </Container>
        </Box>

        {/* Footer */}
        <Box style={{ background: 'white', borderTop: '1px solid #e9ecef' }}>
          <Container size="lg" py={rem(80)}>
            {/* Footer Grid */}
            <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing={rem(40)} mb={rem(60)}>
              {/* Product Column */}
              <Stack gap={rem(20)}>
                <Text fw={700} size="lg">youvidgen AI</Text>
                <Stack gap={rem(12)}>
                  <Text component="a" href="#" c="dimmed" style={{ textDecoration: 'none' }}>AI Video Generator</Text>
                  <Text component="a" href="#" c="dimmed" style={{ textDecoration: 'none' }}>AI Video Editor</Text>
                  <Text component="a" href="#" c="dimmed" style={{ textDecoration: 'none' }}>Text to Video</Text>
                  <Text component="a" href="#" c="dimmed" style={{ textDecoration: 'none' }}>Faceless Video Generator</Text>
                </Stack>
              </Stack>

              {/* Studio Column */}
              <Stack gap={rem(20)}>
                <Text fw={700} size="lg">youvidgen Studio</Text>
                <Stack gap={rem(12)}>
                  <Text component="a" href="#" c="dimmed" style={{ textDecoration: 'none' }}>Online Video Editor</Text>
                  <Text component="a" href="#" c="dimmed" style={{ textDecoration: 'none' }}>Instagram Reels Maker</Text>
                  <Text component="a" href="#" c="dimmed" style={{ textDecoration: 'none' }}>Clipmaker</Text>
                  <Text component="a" href="#" c="dimmed" style={{ textDecoration: 'none' }}>Intro Maker</Text>
                </Stack>
              </Stack>

              {/* Quick Tools Column */}
              <Stack gap={rem(20)}>
                <Text fw={700} size="lg">Quick Tools</Text>
                <Stack gap={rem(12)}>
                  <Text component="a" href="#" c="dimmed" style={{ textDecoration: 'none' }}>Rotate Video</Text>
                  <Text component="a" href="#" c="dimmed" style={{ textDecoration: 'none' }}>Trim Video</Text>
                  <Text component="a" href="#" c="dimmed" style={{ textDecoration: 'none' }}>Cut Video</Text>
                  <Text component="a" href="#" c="dimmed" style={{ textDecoration: 'none' }}>Video Compressor</Text>
                </Stack>
              </Stack>

              {/* Help Column */}
              <Stack gap={rem(20)}>
                <Text fw={700} size="lg">Help</Text>
                <Stack gap={rem(12)}>
                  <Text component="a" href="#" c="dimmed" style={{ textDecoration: 'none' }}>Live chat 24/7</Text>
                  <Text component="a" href="#" c="dimmed" style={{ textDecoration: 'none' }}>Email Us</Text>
                  <Text component="a" href="#" c="dimmed" style={{ textDecoration: 'none' }}>Tutorials</Text>
                  <Text component="a" href="#" c="dimmed" style={{ textDecoration: 'none' }}>Blog</Text>
                </Stack>
              </Stack>
            </SimpleGrid>

            {/* Bottom Footer */}
            <Group justify="space-between" align="center" style={{ borderTop: '1px solid #e9ecef', paddingTop: rem(32) }}>
              <Group>
                {/* Logo */}
                <Group gap={rem(8)} align="center">
                  <Box
                    style={{
                      width: rem(24),
                      height: rem(24),
                      borderRadius: '50%',
                      background: 'linear-gradient(45deg, #8B5CF6, #EC4899)',
                    }}
                  />
                  <Text fw={700}>youvidgen AI</Text>
                </Group>
                <Text size="sm" c="dimmed">© 2024 youvidgen AI. All rights reserved.</Text>
              </Group>

              {/* Social Links */}
              <Group gap={rem(16)}>
                <Text component="a" href="#" c="dimmed" style={{ textDecoration: 'none' }}>Twitter</Text>
                <Text component="a" href="#" c="dimmed" style={{ textDecoration: 'none' }}>LinkedIn</Text>
                <Text component="a" href="#" c="dimmed" style={{ textDecoration: 'none' }}>Instagram</Text>
                <Text component="a" href="#" c="dimmed" style={{ textDecoration: 'none' }}>YouTube</Text>
              </Group>
            </Group>
          </Container>
        </Box>
      </Container>
    </MantineProvider>
  );
}

export default App;
